import React, { useState } from 'react';
import { Button, Divider, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import Description from '../CollapsibleSections/PossibleTreatmentNeedsSection/icons/description.svg';
import Notepad from '../CollapsibleSections/PossibleTreatmentNeedsSection/icons/notepad.svg';
import Flag from '../CollapsibleSections/PossibleTreatmentNeedsSection/icons/flag.svg';

import styles from '../styles.module.scss';
import { ICase, Status } from '@brands/services/cases/types/ICase';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { formatDate } from '@brands/Utils/formatDate';
import { getUserRoleName } from '@brands/Utils/getFriendlyName';
import linkifyHtml from 'linkify-html';
import sanitizeHtml from 'sanitize-html';
import { displayErrorDetails } from '@brands/Utils/displayError';
import ConfirmActionModal from '../Modals/ConfirmActionModal/ConfirmActionModal';
import {
  CareCoordinationReportBenefitsNavigation,
  CareCoordinationReportNotes,
} from '@brands/services/careCoordinationReport/types/types';
import { deleteCareCoordinationReportObject } from '@brands/services/careCoordinationReport/deleteCareCoordinationReportObject';

type ActiveItem =
  | {
      type: 'note';
      item: CareCoordinationReportNotes;
    }
  | {
      type: 'benefits_navigation';
      item: CareCoordinationReportBenefitsNavigation;
    }
  | null;

type Props = {
  thisCase: ICase;
  userInfo: UserProfile;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  openInternalNotesModal: () => void;
  openCareCoordinationNotesModal: (note?: CareCoordinationReportNotes | null) => void;
  openBenefitsNavigationModal: (benefit?: CareCoordinationReportBenefitsNavigation | null) => void;
};

const CareCoordinationReportCard = ({
  thisCase,
  userInfo,
  setCase,
  openInternalNotesModal,
  openCareCoordinationNotesModal,
  openBenefitsNavigationModal,
}: Props): JSX.Element => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeItem, setActiveItem] = useState<ActiveItem>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isAssignedCA =
    userInfo.role.name === UserRoleName.CareAdvisor && Number(userInfo.id) === Number(thisCase.care_advisor_id);

  const canEdit = isAssignedCA && thisCase?.status === Status.InProgressCareAdvisor;

  // Internal notes double as chart addendums, so the assigned CA can still add
  // them after the case is completed, unlike the rest of the report.
  const canAddInternalNote =
    isAssignedCA && (thisCase.status === Status.InProgressCareAdvisor || thisCase.status === Status.Completed);

  const internalNotes = thisCase.care_coordination_report?.internal_notes || [];
  const careCoordinationNotes = thisCase.care_coordination_report?.notes || [];
  const benefitsNavigations = thisCase.care_coordination_report?.benefits_navigations || [];

  const hasInternalNotes = internalNotes.length > 0;
  const hasCareCoordinationNotes = careCoordinationNotes.length > 0;
  const hasBenefitsNavigation = benefitsNavigations.length > 0;

  const sanitizeDisplayHtml = (html: string): string => {
    const linked = linkifyHtml(html || '', {
      defaultProtocol: 'https',
      attributes: { target: '_blank', rel: 'noopener noreferrer' },
    });

    return sanitizeHtml(linked, {
      allowedTags: ['p', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'br', 'span', 'a'],
      allowedAttributes: {
        a: ['href', 'target', 'rel'],
        span: ['class'],
        li: ['data-list'],
      },
    });
  };

  const sortByCreatedAtAsc = <T extends { created_at: string }>(items: T[]): T[] =>
    [...items].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);

      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        return 0;
      }

      return dateA.getTime() - dateB.getTime();
    });

  const handleAddInternalNotes = (): void => {
    if (!canAddInternalNote) return;
    openInternalNotesModal();
  };

  const handleAddCareCoordinationNotes = (): void => {
    if (!canEdit) return;
    openCareCoordinationNotesModal(null);
  };

  const handleAddBenefitsNavigation = (): void => {
    if (!canEdit) return;
    openBenefitsNavigationModal(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: NonNullable<ActiveItem>): void => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setActiveItem(item);
  };

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  };

  const handleEditItem = (): void => {
    if (!activeItem) return;
    handleMenuClose();
    if (activeItem.type === 'note') {
      openCareCoordinationNotesModal(activeItem.item);
    } else {
      openBenefitsNavigationModal(activeItem.item);
    }
  };

  const handleDeleteItem = (): void => {
    if (!activeItem) return;
    handleMenuClose();
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!activeItem) return;
    const reportId = thisCase.care_coordination_report?.id?.toString();
    if (!reportId) return;

    try {
      setIsDeleting(true);
      const objectType = activeItem.type === 'note' ? 'note' : 'benefits_navigation';
      await deleteCareCoordinationReportObject(reportId, activeItem.item.id.toString(), objectType);

      setCase((prev) => {
        const report = prev.care_coordination_report;
        if (!report) return prev;
        return {
          ...prev,
          care_coordination_report: {
            ...report,
            notes:
              activeItem.type === 'note'
                ? (report.notes || []).filter((n) => n.id !== activeItem.item.id)
                : report.notes,
            benefits_navigations:
              activeItem.type === 'benefits_navigation'
                ? (report.benefits_navigations || []).filter((b) => b.id !== activeItem.item.id)
                : report.benefits_navigations,
          },
        };
      });

      setIsDeleteModalOpen(false);
      setActiveItem(null);
    } catch (error) {
      displayErrorDetails('Failed to delete item');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={styles.consultationPageRightSideFrameCaseDetails}>
      <div className={styles.consultationPageRightSideFrameCaseDetailsTitle} onClick={() => setIsOpen((p) => !p)}>
        <div className={styles.consultationPageRightSideFrameCaseDetailsTitleDiv}>
          <span>Care Coordination report</span>

          <IconButton size="small">
            <KeyboardArrowDownIcon
              style={{
                transition: '0.25s',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </IconButton>
        </div>

        <Divider orientation="horizontal" flexItem className={styles.divider} />
      </div>

      {isOpen && (
        <div className={styles.oralHealthCoachingReportBody}>
          <div
            className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
              hasInternalNotes ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
            }`}
          >
            <div
              className={`${styles.oralHealthCoachingReportItem} ${styles.possibleTreatmentNeedsSectionDivChildRow}`}
              onClick={handleAddInternalNotes}
              style={{ opacity: canAddInternalNote ? 1 : 0.5, pointerEvents: canAddInternalNote ? 'auto' : 'none' }}
            >
              <Tooltip title={!isAssignedCA ? 'Only assigned Care Advisor can edit' : ''}>
                <span className={styles.possibleTreatmentNeedsSectionDivChildInternalNotes}>
                  <img src={Description} />
                </span>
              </Tooltip>

              {!hasInternalNotes ? (
                <Button variant="text">Add internal case notes</Button>
              ) : (
                <div className={styles.existDataDiv}>
                  <span>Internal case notes</span>
                  <Button variant="text">
                    <AddIcon /> Add
                  </Button>
                </div>
              )}
            </div>

            {hasInternalNotes && (
              <div className={styles.noteBody}>
                {sortByCreatedAtAsc(internalNotes).map((note, index) => (
                  <React.Fragment key={note.id ?? index}>
                    <Divider orientation="horizontal" flexItem className={styles.divider} />
                    <div className={`fs-exclude ${styles.caseNotesContent}`}>
                      <div className={`fs-exclude ${styles.caseNotesContentHeader}`}>
                        <span>{formatDate(note.created_at) || ''}</span>
                        <Divider orientation="vertical" flexItem className={styles.divider} />
                        <span>
                          {note?.created_by?.full_name} ({getUserRoleName(note?.created_by?.role.name)})
                        </span>
                      </div>

                      <div className={`fs-exclude ${styles.caseNotesContentContent}`}>
                        <span
                          className={styles.caseNotesContentText}
                          dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(note.note) }}
                        />
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          <div
            className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
              hasCareCoordinationNotes ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
            }`}
          >
            <div
              className={`${styles.oralHealthCoachingReportItem} ${styles.possibleTreatmentNeedsSectionDivChildRow}`}
              onClick={handleAddCareCoordinationNotes}
              style={{ opacity: canEdit ? 1 : 0.5, pointerEvents: canEdit ? 'auto' : 'none' }}
            >
              <Tooltip title={!canEdit ? 'Only assigned Care Advisor can edit' : ''}>
                <span className={styles.possibleTreatmentNeedsSectionDivChildConsultationSummary}>
                  <img src={Notepad} />
                </span>
              </Tooltip>

              {!hasCareCoordinationNotes ? (
                <Button variant="text">Add Care Coordination notes</Button>
              ) : (
                <div className={styles.existDataDiv}>
                  <span>Care Coordination notes</span>
                  {/*                   <Button variant="text">
                    <AddIcon /> Add
                  </Button> */}
                </div>
              )}
            </div>

            {hasCareCoordinationNotes && (
              <div className={styles.consultationSummaryBody}>
                {sortByCreatedAtAsc(careCoordinationNotes).map((note, index) => (
                  <React.Fragment key={note.id ?? index}>
                    <Divider orientation="horizontal" flexItem className={styles.divider} />

                    <div className={styles.consultationSummaryBodyContent}>
                      <div className={styles.caseNotesContent}>
                        <div className={styles.caseNotesContentHeader}>
                          <span>{formatDate(note.created_at) || ''}</span>
                          <Divider orientation="vertical" flexItem className={styles.divider} />
                          <span>
                            {note?.created_by?.full_name} ({getUserRoleName(note?.created_by?.role.name)})
                          </span>
                        </div>

                        <div
                          className={styles.consultationSummaryContent}
                          dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(note.note) }}
                        />
                      </div>
                      <div>
                        {canEdit && (
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, { type: 'note', item: note })}>
                            <MoreHorizIcon className={styles.moreIcon} />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          <div
            className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
              hasBenefitsNavigation ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
            }`}
          >
            <div
              className={`${styles.oralHealthCoachingReportItem} ${styles.possibleTreatmentNeedsSectionDivChildRow}`}
              onClick={handleAddBenefitsNavigation}
              style={{ opacity: canEdit ? 1 : 0.5, pointerEvents: canEdit ? 'auto' : 'none' }}
            >
              <Tooltip title={!canEdit ? 'Only assigned Care Advisor can edit' : ''}>
                <span className={styles.possibleTreatmentNeedsSectionDivChildGoal}>
                  <img src={Flag} />
                </span>
              </Tooltip>

              {!hasBenefitsNavigation ? (
                <Button variant="text">Add Benefits Navigation</Button>
              ) : (
                <div className={styles.existDataDiv}>
                  <span>Benefits Navigation</span>
                  {/* <Button variant="text">
                    <AddIcon /> Add
                  </Button> */}
                </div>
              )}
            </div>

            {hasBenefitsNavigation && (
              <div className={styles.consultationSummaryBody}>
                {sortByCreatedAtAsc(benefitsNavigations).map((benefit, index) => (
                  <React.Fragment key={benefit.id ?? index}>
                    <Divider orientation="horizontal" flexItem className={styles.divider} />

                    <div className={styles.consultationSummaryBodyContent}>
                      <div className={styles.caseNotesContent}>
                        <div className={styles.caseNotesContentHeader}>
                          <span>{formatDate(benefit.created_at) || ''}</span>
                          <Divider orientation="vertical" flexItem className={styles.divider} />
                          <span>
                            {benefit?.created_by?.full_name} ({getUserRoleName(benefit?.created_by?.role.name)})
                          </span>
                        </div>

                        <div
                          className={styles.consultationSummaryContent}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeDisplayHtml(benefit.benefits_navigation),
                          }}
                        />
                      </div>
                      <div>
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, { type: 'benefits_navigation', item: benefit })}
                          >
                            <MoreHorizIcon className={styles.moreIcon} />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEditItem}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteItem} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      <ConfirmActionModal
        open={isDeleteModalOpen}
        title={activeItem?.type === 'note' ? 'Delete Care Coordination note' : 'Delete Benefits Navigation'}
        message={(() => {
          if (!activeItem) return 'Are you sure you want to delete this item?';
          const raw = activeItem.type === 'note' ? activeItem.item.note : activeItem.item.benefits_navigation;
          const text = new DOMParser().parseFromString(raw || '', 'text/html').body.textContent?.trim() || '';
          const preview = text.length > 60 ? `${text.slice(0, 60)}…` : text;
          return `Are you sure you want to delete "${preview || 'this item'}"?`;
        })()}
        confirmText="Delete"
        cancelText="Cancel"
        destructive
        isLoading={isDeleting}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default CareCoordinationReportCard;
